/**
 * BroadcastScheduleEditor
 *
 * コンテンツ単位の放送スケジュール (`BroadcastSchedule`) を編集する UI。
 *
 * - 曜日 (日〜土) の 7 チェックボックス。0 件チェック時は「全曜日で放送」として扱う (schedule 側は
 *   `days_of_week` を undefined にして送る)。
 * - 時間帯 (start_time / end_time) の可変長リスト。0 件時は「24 時間放送」バッジ。
 *   日跨ぎ (`start > end`) を許容し、空欄はエラー表示。
 * - 有効期間 (start_date / end_date) の `<input type="date">`。両方未入力なら「期限なし」バッジ。
 *   `start > end` の場合はエラー表示。
 *
 * 初期表示は `<details>` で折りたたむ。各コンテンツカード側で `content.schedule === undefined` の
 * 場合は「常時放送」バッジをカード見出しに表示する (バッジは呼び出し元 UI の責務)。
 *
 * ## onChange の意味
 * すべてのフィールドが未指定 (`days_of_week` 空 & `time_ranges` 空 & 日付両方未入力) になったら、
 * `onChange(undefined)` を呼び、コンテンツから schedule プロパティ自体を消す (=常時放送)。
 * 1 つでも指定されていれば `onChange({...})` で保持する。
 *
 * ## サーバ / 端末との整合性
 * - サーバ側 (`lambda/control-panel-api/index.py` の `_normalize_schedule`) と同等の
 *   バリデーションルール (曜日 enum / `HH:mm` / start<=end) を UI 側でも先行チェックし、
 *   ユーザに即時フィードバックする。
 * - 端末側 (SBI-B/C 担当) はローカル時刻でリアルタイム判定する。詳細は
 *   `docs/broadcast-schedule.md` を参照。
 */
import { useState } from 'react';

import type { BroadcastSchedule, DayOfWeek, TimeRange } from '../api/types';
import { HintTooltip } from './HintTooltip';

/** 曜日の並び順 (sun→sat)。UI 表示・正規化とも本順序に統一する。 */
const DAY_ORDER: readonly DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** 曜日ラベル (日本語 1 文字) */
const DAY_LABEL: Record<DayOfWeek, string> = {
  sun: '日',
  mon: '月',
  tue: '火',
  wed: '水',
  thu: '木',
  fri: '金',
  sat: '土',
};

/** `HH:mm` (24 時間表記) を検証する正規表現。サーバ側 `_TIME_PATTERN` と揃える。 */
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** `YYYY-MM-DD` を検証する正規表現。 */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export interface BroadcastScheduleEditorProps {
  /**
   * 現在の schedule 値。未設定 (常時放送) の場合は `undefined`。
   */
  value: BroadcastSchedule | undefined;
  /**
   * 値変更時のコールバック。
   * すべてのフィールドが空になった場合は `undefined` を渡し、schedule プロパティごと消す想定。
   */
  onChange: (next: BroadcastSchedule | undefined) => void;
  /**
   * カード見出し / data-testid の一意化に使うインデックス (例: 親コンテンツの `index`)。
   * 同一画面に複数の Editor が並ぶことを想定し、各 testid に `-${contentIndex}` を付ける。
   */
  contentIndex: number;
  /**
   * `<details>` を初期展開したい場合は true。既定は false (折りたたみ)。
   * E2E / テストからは true を渡してすぐ操作できるようにする。
   */
  defaultOpen?: boolean;
}

/**
 * 「schedule のフィールドが全部未指定か」を判定する。
 *
 * `days_of_week` 空 (未指定 or 空配列) & `time_ranges` 空 & `start_date` / `end_date` 未入力 なら
 * 常時放送とみなし、schedule プロパティごと消す (=`onChange(undefined)`)。
 */
function isScheduleEmpty(s: BroadcastSchedule | undefined): boolean {
  if (!s) return true;
  const noDays = !s.days_of_week || s.days_of_week.length === 0;
  const noTimes = !s.time_ranges || s.time_ranges.length === 0;
  const noStart = !s.start_date;
  const noEnd = !s.end_date;
  return noDays && noTimes && noStart && noEnd;
}

/**
 * `days_of_week` を sun→sat 順に並び替えて重複除去する。
 * サーバ側 `_normalize_schedule` と同じ正規化を UI 送信直前に適用し、
 * 保存・再取得後の並び順を一致させる。
 */
function sortDays(days: readonly DayOfWeek[]): DayOfWeek[] {
  const set = new Set(days);
  return DAY_ORDER.filter((d) => set.has(d));
}

/**
 * schedule に patch を当てて空判定 → `onChange(next | undefined)` する共通ヘルパー。
 * 呼び出し側で毎回「全項目未指定なら undefined を渡す」の実装が重複するのを避ける。
 */
function emitSchedule(
  current: BroadcastSchedule | undefined,
  patch: Partial<BroadcastSchedule>,
  onChange: (next: BroadcastSchedule | undefined) => void,
): void {
  const next: BroadcastSchedule = { ...(current ?? {}), ...patch };
  // undefined / 空配列 / 空文字 は不要フィールドとして削除
  if (!next.days_of_week || next.days_of_week.length === 0) delete next.days_of_week;
  if (!next.time_ranges || next.time_ranges.length === 0) delete next.time_ranges;
  if (!next.start_date) delete next.start_date;
  if (!next.end_date) delete next.end_date;
  if (isScheduleEmpty(next)) {
    onChange(undefined);
  } else {
    onChange(next);
  }
}

/**
 * schedule 設定サマリを短い文字列で返す。ボタン表示に使う。
 * - 未設定: "常時放送"
 * - 設定あり: "月火水木金 / 07:00-10:00 / 2026-04-01〜2026-04-30" のような形
 *   (未設定の要素は省略。すべて設定済みなら 3 セグメント連結。)
 */
function formatScheduleSummary(value: BroadcastSchedule | undefined): string {
  if (!value || isScheduleEmpty(value)) return '常時放送';
  const segments: string[] = [];
  if (value.days_of_week && value.days_of_week.length > 0) {
    // 曜日は sun→sat 順に並び替えて 1 文字ずつ連結 (例: "月火水木金")
    segments.push(
      sortDays(value.days_of_week)
        .map((d) => DAY_LABEL[d])
        .join(''),
    );
  } else {
    segments.push('全曜日');
  }
  if (value.time_ranges && value.time_ranges.length > 0) {
    // 各時間帯を "07:00-10:00" 形式で連結し、複数あればカンマで区切る
    segments.push(value.time_ranges.map((r) => `${r.start_time}-${r.end_time}`).join(','));
  } else {
    segments.push('24時間');
  }
  if (value.start_date || value.end_date) {
    const s = value.start_date ?? '';
    const e = value.end_date ?? '';
    segments.push(`${s}〜${e}`);
  }
  return segments.join(' / ');
}

/**
 * BroadcastSchedule 編集用の React コンポーネント。
 */
export function BroadcastScheduleEditor({
  value,
  onChange,
  contentIndex,
  defaultOpen = false,
}: BroadcastScheduleEditorProps): React.JSX.Element {
  // 展開状態は内部 state で管理する。
  // defaultOpen=true (E2E / テスト用途) の場合は初期展開する。
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

  const days: DayOfWeek[] = value?.days_of_week ? sortDays(value.days_of_week) : [];
  const timeRanges: TimeRange[] = value?.time_ranges ?? [];
  const startDate = value?.start_date ?? '';
  const endDate = value?.end_date ?? '';
  const isEmpty = isScheduleEmpty(value);
  const summary = formatScheduleSummary(value);

  // -----------------------------------------------------------------
  // ハンドラ (patch を作って emitSchedule に流す)
  // -----------------------------------------------------------------

  const toggleDay = (day: DayOfWeek): void => {
    const nextSet = new Set(days);
    if (nextSet.has(day)) {
      nextSet.delete(day);
    } else {
      nextSet.add(day);
    }
    const nextDays = sortDays([...nextSet]);
    emitSchedule(value, { days_of_week: nextDays.length > 0 ? nextDays : undefined }, onChange);
  };

  const addTimeRange = (): void => {
    const nextRanges: TimeRange[] = [...timeRanges, { start_time: '', end_time: '' }];
    emitSchedule(value, { time_ranges: nextRanges }, onChange);
  };

  const updateTimeRange = (index: number, patch: Partial<TimeRange>): void => {
    const nextRanges = timeRanges.map((r, i) => (i === index ? { ...r, ...patch } : r));
    emitSchedule(value, { time_ranges: nextRanges }, onChange);
  };

  const removeTimeRange = (index: number): void => {
    const nextRanges = timeRanges.filter((_, i) => i !== index);
    emitSchedule(value, { time_ranges: nextRanges.length > 0 ? nextRanges : undefined }, onChange);
  };

  const setStartDate = (v: string): void => {
    emitSchedule(value, { start_date: v || undefined }, onChange);
  };
  const setEndDate = (v: string): void => {
    emitSchedule(value, { end_date: v || undefined }, onChange);
  };

  // -----------------------------------------------------------------
  // クライアント側バリデーション (即時フィードバック用)
  // - 空欄・形式不一致の time_range
  // - start_date > end_date
  // サーバ側 `_normalize_schedule` と同ルール。エラー時は該当行の直下に表示する。
  // -----------------------------------------------------------------

  const timeRangeErrors: (string | null)[] = timeRanges.map((r) => {
    const st = r.start_time;
    const et = r.end_time;
    if (!st || !et) return '開始時刻・終了時刻の両方を入力してください';
    if (!TIME_PATTERN.test(st) || !TIME_PATTERN.test(et)) {
      return '時刻は HH:mm (24時間表記) で入力してください';
    }
    if (st === et) return '開始時刻と終了時刻が同一です (無効な範囲)';
    return null;
  });

  const dateError: string | null = (() => {
    if (startDate && !DATE_PATTERN.test(startDate))
      return '開始日は YYYY-MM-DD 形式で入力してください';
    if (endDate && !DATE_PATTERN.test(endDate)) return '終了日は YYYY-MM-DD 形式で入力してください';
    if (startDate && endDate && startDate > endDate)
      return '開始日は終了日以前の日付を指定してください';
    return null;
  })();

  // -----------------------------------------------------------------
  // 描画
  // -----------------------------------------------------------------

  return (
    <div
      className="broadcast-schedule-editor"
      data-testid={`schedule-editor-${contentIndex}`}
      style={{ margin: '8px 0' }}
    >
      {/*
       * スケジュール設定を「見つけやすい・開きやすい」よう、`<details>` ではなく
       * 明示的なボタン (青系背景 + 📅 絵文字 + 現状サマリ) + 展開エリアで構成する。
       *
       * - `<button aria-expanded={isOpen}>` として、開閉状態をアクセシビリティに公開。
       * - サマリ (曜日 / 時間帯 / 有効期間) をボタン内バッジに表示することで、
       *   展開しなくても現在の設定状態が一目でわかる (「常時放送」ならその旨バッジ)。
       * - 未設定 (schedule=undefined) と設定済みで見た目 (背景色) を微調整して、
       *   「ここは編集可能なコントロールですよ」というアフォーダンスを強調する。
       */}
      <button
        type="button"
        className="broadcast-schedule-toggle"
        data-testid={`schedule-toggle-${contentIndex}`}
        aria-expanded={isOpen}
        aria-controls={`schedule-panel-${contentIndex}`}
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          background: isEmpty ? '#f0f4f8' : '#e6f0fb',
          border: `1px solid ${isEmpty ? '#d0d7de' : '#a4c7f0'}`,
          borderRadius: 6,
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
          color: '#24292f',
        }}
      >
        <span aria-hidden="true" style={{ fontSize: '1.1em' }}>
          📅
        </span>
        <strong style={{ marginRight: 4 }}>スケジュール設定</strong>
        <span
          data-testid={`schedule-summary-${contentIndex}`}
          style={{
            padding: '2px 8px',
            background: isEmpty ? '#e6f4ea' : '#fff',
            color: isEmpty ? '#137333' : '#0969da',
            border: `1px solid ${isEmpty ? '#b7dfb9' : '#a4c7f0'}`,
            borderRadius: 12,
            fontSize: '0.85em',
            lineHeight: 1.4,
          }}
        >
          {summary}
        </span>
        <span aria-hidden="true" style={{ marginLeft: 'auto', fontSize: '0.85em' }}>
          {isOpen ? '▲' : '▼'}
        </span>
      </button>
      {/*
       * 展開エリア。isOpen が true のときのみ描画する (未展開時は DOM から外して
       * フォームフォーカスの妨げにならないようにする)。
       */}
      {!isOpen ? null : (
        <div
          id={`schedule-panel-${contentIndex}`}
          data-testid={`schedule-panel-${contentIndex}`}
          /*
           * レイアウトは CSS (`.schedule-panel-grid`) に寄せる。
           * 「放送する曜日 / 放送する時間帯 / 有効期間」を横 3 段組で並べ、
           * 中幅 (<=1099px) では 2 列、狭幅 (<=768px) では 1 列へフォールバックする
           * (`.form-grid` / `.contents-fields` と同じ流儀)。
           */
          className="schedule-panel-grid"
          style={{
            padding: '8px 4px',
            border: '1px solid #d0d7de',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            marginTop: -1,
          }}
        >
          {/* --- 曜日 --- */}
          <fieldset style={{ border: '1px solid #d0d7de', padding: 8 }}>
            <legend>放送する曜日</legend>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DAY_ORDER.map((day) => (
                <label key={day} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  <input
                    type="checkbox"
                    data-testid={`schedule-days-${contentIndex}-${day}`}
                    checked={days.includes(day)}
                    onChange={() => toggleDay(day)}
                  />
                  {DAY_LABEL[day]}
                </label>
              ))}
            </div>
            {days.length === 0 ? (
              <p
                className="hint"
                data-testid={`schedule-days-all-hint-${contentIndex}`}
                style={{ margin: '4px 0 0' }}
              >
                曜日未選択 = <strong>全曜日で放送</strong>
              </p>
            ) : null}
          </fieldset>

          {/* --- 時間帯 --- */}
          <fieldset style={{ border: '1px solid #d0d7de', padding: 8 }}>
            <legend>
              放送する時間帯
              <HintTooltip label="時間帯の説明" testId={`schedule-overnight-hint-${contentIndex}`}>
                開始 {'>'} 終了は<strong>日跨ぎ</strong>として扱います (例: 22:00〜05:00)。
              </HintTooltip>
            </legend>
            {timeRanges.length === 0 ? (
              <p
                className="hint"
                data-testid={`schedule-time-24h-hint-${contentIndex}`}
                style={{ margin: '4px 0' }}
              >
                時間帯未指定 = <strong>24 時間放送</strong>
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {timeRanges.map((r, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
                    >
                      <input
                        type="time"
                        aria-label={`時間帯${i + 1} 開始時刻`}
                        data-testid={`schedule-time-start-${contentIndex}-${i}`}
                        value={r.start_time}
                        onChange={(e) => updateTimeRange(i, { start_time: e.target.value })}
                      />
                      <span>〜</span>
                      <input
                        type="time"
                        aria-label={`時間帯${i + 1} 終了時刻`}
                        data-testid={`schedule-time-end-${contentIndex}-${i}`}
                        value={r.end_time}
                        onChange={(e) => updateTimeRange(i, { end_time: e.target.value })}
                      />
                      <button
                        type="button"
                        className="danger"
                        aria-label={`時間帯${i + 1} を削除`}
                        data-testid={`schedule-remove-time-range-${contentIndex}-${i}`}
                        onClick={() => removeTimeRange(i)}
                      >
                        − 削除
                      </button>
                    </div>
                    {timeRangeErrors[i] ? (
                      <div
                        role="alert"
                        data-testid={`schedule-time-error-${contentIndex}-${i}`}
                        style={{ color: '#d1242f', fontSize: '0.85em', marginTop: 2 }}
                      >
                        {timeRangeErrors[i]}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              data-testid={`schedule-add-time-range-${contentIndex}`}
              onClick={addTimeRange}
            >
              ＋ 時間帯を追加
            </button>
          </fieldset>

          {/* --- 有効期間 --- */}
          <fieldset style={{ border: '1px solid #d0d7de', padding: 8 }}>
            <legend>有効期間</legend>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span>開始日</span>
                <input
                  type="date"
                  aria-label="開始日"
                  data-testid={`schedule-start-date-${contentIndex}`}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <span>〜</span>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span>終了日</span>
                <input
                  type="date"
                  aria-label="終了日"
                  data-testid={`schedule-end-date-${contentIndex}`}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </label>
            </div>
            {!startDate && !endDate ? (
              <p
                className="hint"
                data-testid={`schedule-date-no-limit-hint-${contentIndex}`}
                style={{ margin: '4px 0 0' }}
              >
                有効期間未指定 = <strong>期限なし</strong>
              </p>
            ) : null}
            {dateError ? (
              <div
                role="alert"
                data-testid={`schedule-date-error-${contentIndex}`}
                style={{ color: '#d1242f', fontSize: '0.85em', marginTop: 4 }}
              >
                {dateError}
              </div>
            ) : null}
          </fieldset>
        </div>
      )}
    </div>
  );
}
